import React, { useState } from 'react';
import {
  BarChart3,
  MessageSquare,
  Settings,
  Users,
  Calendar,
  Send,
  History,
  Smartphone
} from 'lucide-react';
import CommunicationDashboard from './CommunicationDashboard';
import TemplateManagement from './TemplateManagement';
import AppointmentReminders from './AppointmentReminders';
import SendMessage from './SendMessage';
import CommunicationHistory from './CommunicationHistory';
import PatientPreferences from './PatientPreferences';
import WhatsAppDashboard from '../appointments/WhatsAppDashboard';

type TabType = 'dashboard' | 'templates' | 'reminders' | 'send' | 'logs' | 'preferences' | 'whatsapp';

export default function CommunicationCenter() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');

  const tabs = [
    { 
      key: 'dashboard' as TabType, 
      label: 'Tableau de bord', 
      icon: BarChart3,
      description: 'Vue d\'ensemble des communications'
    },
    { 
      key: 'reminders' as TabType, 
      label: 'Rappels RDV', 
      icon: Calendar,
      description: 'Gérer les rappels automatiques'
    },
    { 
      key: 'templates' as TabType, 
      label: 'Templates', 
      icon: Settings,
      description: 'Modèles de messages'
    },
    { 
      key: 'send' as TabType, 
      label: 'Envoyer', 
      icon: Send,
      description: 'Envoyer des messages manuels'
    },
    { 
      key: 'logs' as TabType, 
      label: 'Historique', 
      icon: History,
      description: 'Historique des communications'
    },
    {
      key: 'preferences' as TabType,
      label: 'Préférences',
      icon: Users,
      description: 'Préférences des patients'
    },
    {
      key: 'whatsapp' as TabType,
      label: 'WhatsApp',
      icon: Smartphone,
      description: 'Sessions, logs et configuration Evolution API'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <CommunicationDashboard />;
      case 'templates':
        return <TemplateManagement />;
      case 'reminders':
        return <AppointmentReminders />;
      case 'send':
        return <SendMessage />;
      case 'logs':
        return <CommunicationHistory />;
      case 'preferences':
        return <PatientPreferences />;
      case 'whatsapp':
        return <WhatsAppDashboard />;
      default:
        return <CommunicationDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Centre de Communications</h1>
          <p className="text-gray-600 mt-2">
            Gérez toutes vos communications avec les patients depuis un seul endroit
          </p>
        </div>

        {/* Navigation par onglets */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Description de l'onglet actif */}
          <div className="px-6 py-3 bg-gray-50">
            <p className="text-sm text-gray-600">
              {tabs.find(tab => tab.key === activeTab)?.description}
            </p>
          </div>
        </div>

        {/* Contenu de l'onglet */}
        <div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

